import {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

/**
 * Lets the detail inside a peek put its own buttons in the panel's chrome
 * bar, beside the expand and close controls.
 *
 * The detail is `children` — it renders below the chrome and cannot reach
 * it. Its status actions therefore sat as the first row of the scrolling
 * body, which meant "Close out job" scrolled away the moment anybody read
 * the job. The full page solved this years ago with a topbar slot; this
 * is the same trick for the panel, kept small because a peek shows one
 * detail at a time.
 *
 * Registrations are keyed by id and the last one wins, so a drill-down
 * that replaces the body swaps the buttons with it and takes them along
 * when it leaves. Anything outside a `PeekPanel` no-ops rather than
 * throwing — the same detail renders full-page, where the topbar owns
 * this instead.
 *
 * <p><b>The node is PORTALLED, not handed up.</b> It used to travel to
 * the chrome through the panel's own state, which rendered it in the
 * header's position in the React tree — outside every provider the body
 * sits inside. So a header button could not see the peek's own
 * navigation context: "Open invoice" on a work order fell through to
 * `navigate()` and threw the manager out of the panel they were reading,
 * into the invoices section. A portal renders the node where it was
 * written and only moves it in the DOM, so context reaches it.
 *
 * <p>That also removes the feedback loop the old shape had to be careful
 * about: nothing about the node goes into state any more, so a caller who
 * forgets to memoize cannot make the panel re-render itself.
 */

interface PeekActionsApi {
  /** Where the chrome wants the buttons. Null until the header mounts. */
  readonly slot: HTMLElement | null
  /** Take the slot. The most recent claimant renders. */
  readonly claim: (id: string) => void
  readonly release: (id: string) => void
  /** Who currently owns it — everyone else renders nothing. */
  readonly topId: string | null
}

const PeekActionsApiContext = createContext<PeekActionsApi | null>(null)

/**
 * Panel-side. Returns the API to hand to the body, and the ref to put on
 * the element the buttons should appear in.
 */
export function usePeekActionsRegistry(): {
  readonly api: PeekActionsApi
  readonly slotRef: (el: HTMLElement | null) => void
} {
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  const [ids, setIds] = useState<readonly string[]>([])

  const claim = useCallback((id: string) => {
    setIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }, [])
  const release = useCallback((id: string) => {
    setIds((prev) => prev.filter((e) => e !== id))
  }, [])

  const api = useMemo<PeekActionsApi>(
    () => ({ slot, claim, release, topId: ids.length > 0 ? ids[ids.length - 1]! : null }),
    [slot, claim, release, ids],
  )

  // A callback ref, not `useRef`: the element arrives after the first
  // render and nothing would re-read a ref object, so the portal would
  // never open on the first paint of a freshly opened panel.
  const slotRef = useCallback((el: HTMLElement | null) => setSlot(el), [])

  return { api, slotRef }
}

/** Hands the API to the panel body. */
export function PeekActionsProvider({
  api,
  children,
}: {
  readonly api: PeekActionsApi
  readonly children: ReactNode
}) {
  return <PeekActionsApiContext.Provider value={api}>{children}</PeekActionsApiContext.Provider>
}

/**
 * Detail-side. RENDER what this returns — it is the portal that puts the
 * node in the chrome, and dropping it on the floor leaves the header
 * empty.
 *
 * <p>Returns null outside a peek, before the header has mounted, and for
 * any registrant that is no longer the top one — a drill-down owns the
 * slot while it is open, and the detail underneath goes quiet rather than
 * rendering its buttons into the same row.
 */
export function usePeekActions(node: ReactNode): ReactNode {
  const api = useContext(PeekActionsApiContext)
  const id = useId()

  // Claim on mount only. The node itself is not state any more, so
  // nothing here runs when the buttons change — which is what stops a
  // detail refreshing its own actions from climbing over a drill-down.
  useLayoutEffect(() => {
    if (!api) return
    api.claim(id)
    return () => api.release(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api?.claim, api?.release, id])

  if (!api?.slot || api.topId !== id) return null
  return createPortal(node, api.slot)
}

/**
 * Render-guarded form of {@link usePeekActions}. Render it where the
 * chrome should carry actions and leave it out where it should not —
 * mounting it with `null` is a write that blanks the slot, not an
 * abstention.
 */
export function PeekActions({ node }: { readonly node: ReactNode }) {
  return <>{usePeekActions(node)}</>
}
